import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Alert, Button, Col, Form, FormGroup, Input, Label, Row, Table } from 'reactstrap'
import axios from 'axios'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowLeft, faEdit, faPlusSquare, faTrash } from '@fortawesome/free-solid-svg-icons'

import { tokenConfig } from '../utils/apiConfig'
import AddSessions from '../components/EventList/AddSessions'
import LocationPicker from '../components/LocationPicker'

const CreateEvent = () => {
    const navigate = useNavigate()

    const [name, setName] = useState('')
    const [description, setDescription] = useState('')
    const [status, setStatus] = useState('')
    const [locationName, setLocationName] = useState('')
    const [locationPlaceId, setLocationPlaceId] = useState('')
    const [difficulty, setDifficulty] = useState('')
    const [capacity, setCapacity] = useState('')
    const [saving, setSaving] = useState(false)
    const [eventError, setEventError] = useState('')
    const [savedEvent, setSavedEvent] = useState(null)

    const [sessions, setSessions] = useState([])
    const [sessionModal, setSessionModal] = useState(false)
    const [activeSession, setActiveSession] = useState(null)

    const saveEvent = async (e) => {
        e.preventDefault()
        setSaving(true)
        setEventError('')
        try {
            const res = await axios.post('/api/events', {
                name, description, status, location: locationName, locationPlaceId, difficulty, capacity,
                company_id: '5f44f153841c2929c2eee625',
            }, tokenConfig())
            setSavedEvent(res.data)
        } catch (err) {
            setEventError(err.response?.data?.message || 'Failed to save event')
        } finally {
            setSaving(false)
        }
    }

    const deleteSession = async (_id) => {
        if (!window.confirm('Delete this session?')) return
        await axios.delete(`/api/event-sessions/${_id}`, tokenConfig())
            .then(() => setSessions(prev => prev.filter(s => s._id !== _id)))
            .catch(err => console.log(err))
    }

    return (
        <div className='page-shell page-shell--wide page-section'>

            <div className='page-header'>
                <div>
                    <button
                        className='btn btn-link p-0 mb-2'
                        style={{ color: 'inherit', textDecoration: 'none' }}
                        onClick={() => navigate('/eventlist')}
                    >
                        <FontAwesomeIcon icon={faArrowLeft} style={{ marginRight: '0.4rem' }} />
                        Back to Events
                    </button>
                    <span className='eyebrow'>Admin Events</span>
                    <h1 className='section-title'>Create New Event</h1>
                    <p className='section-lead'>Fill in the event details then add sessions below.</p>
                </div>
            </div>

            {/* ── Step 1: Event Details ── */}
            <div className='fobk-surface-card' style={{ padding: '1.5rem', marginBottom: '1.5rem' }}>
                <h2 style={{ fontSize: '1.05rem', fontWeight: 600, marginBottom: '1.25rem' }}>
                    Event Details
                </h2>

                {eventError && <Alert color='danger'>{eventError}</Alert>}

                <Form onSubmit={saveEvent}>
                    <Row>
                        <Col md={12}>
                            <FormGroup>
                                <Label>Event Name</Label>
                                <Input
                                    type='text'
                                    value={name}
                                    onChange={e => setName(e.target.value)}
                                    disabled={!!savedEvent}
                                    required
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={12}>
                            <FormGroup>
                                <Label>Description</Label>
                                <Input
                                    type='textarea'
                                    rows={3}
                                    value={description}
                                    onChange={e => setDescription(e.target.value)}
                                    disabled={!!savedEvent}
                                    required
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={4}>
                            <FormGroup>
                                <Label>Difficulty</Label>
                                <Input
                                    type='select'
                                    value={difficulty}
                                    onChange={e => setDifficulty(e.target.value)}
                                    disabled={!!savedEvent}
                                    required
                                >
                                    <option value=''>Select…</option>
                                    <option>Beginner</option>
                                    <option>Intermediate</option>
                                    <option>Hard</option>
                                </Input>
                            </FormGroup>
                        </Col>
                        <Col md={4}>
                            <FormGroup>
                                <Label>Status</Label>
                                <Input
                                    type='select'
                                    value={status}
                                    onChange={e => setStatus(e.target.value)}
                                    disabled={!!savedEvent}
                                    required
                                >
                                    <option value=''>Select…</option>
                                    <option>Open</option>
                                    <option>Closed</option>
                                </Input>
                            </FormGroup>
                        </Col>
                        <Col md={4}>
                            <FormGroup>
                                <Label>Capacity</Label>
                                <Input
                                    type='number'
                                    value={capacity}
                                    onChange={e => setCapacity(e.target.value)}
                                    disabled={!!savedEvent}
                                    required
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={12}>
                            <FormGroup>
                                <Label>Location</Label>
                                <LocationPicker
                                    value={locationName}
                                    onChange={({ name, placeId }) => { setLocationName(name); setLocationPlaceId(placeId) }}
                                    disabled={!!savedEvent}
                                    required
                                />
                            </FormGroup>
                        </Col>
                    </Row>

                    {!savedEvent ? (
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '0.5rem' }}>
                            <Button color='secondary' type='button' onClick={() => navigate('/eventlist')}>
                                Cancel
                            </Button>
                            <Button color='primary' type='submit' disabled={saving}>
                                {saving ? 'Saving…' : 'Save & Add Sessions'}
                            </Button>
                        </div>
                    ) : (
                        <Alert color='success' className='mb-0'>
                            Event saved — add sessions below, then click Done when finished.
                        </Alert>
                    )}
                </Form>
            </div>

            {/* ── Step 2: Sessions (unlocked after event save) ── */}
            <div
                className='fobk-surface-card'
                style={{
                    padding: '1.5rem',
                    opacity: savedEvent ? 1 : 0.45,
                    pointerEvents: savedEvent ? 'auto' : 'none',
                }}
            >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
                    <h2 style={{ fontSize: '1.05rem', fontWeight: 600, margin: 0 }}>
                        Sessions
                        {!savedEvent && <span style={{ fontWeight: 400, fontSize: '0.85rem', marginLeft: '0.6rem', color: '#888' }}>— save the event above to unlock</span>}
                    </h2>
                    {savedEvent && (
                        <Button
                            color='primary'
                            onClick={() => { setActiveSession(null); setSessionModal(true) }}
                        >
                            <FontAwesomeIcon icon={faPlusSquare} style={{ marginRight: '0.4rem' }} />
                            Add Session
                        </Button>
                    )}
                </div>

                {savedEvent && (
                    <AddSessions
                        eventId={savedEvent._id}
                        eventSession={activeSession}
                        sessionModal={sessionModal}
                        toggle={() => setSessionModal(false)}
                        addToSessionList={s => setSessions(prev => [...prev, s])}
                        updateSessionList={updated => setSessions(prev => prev.map(s => s._id === updated._id ? updated : s))}
                    />
                )}

                {sessions.length === 0 ? (
                    <p style={{ color: '#888', margin: 0 }}>No sessions yet.</p>
                ) : (
                    <Table className='fobk-data-table'>
                        <thead>
                            <tr>
                                <th>Date</th>
                                <th>Start Time</th>
                                <th>End Time</th>
                                <th>Status</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {sessions.map(session => (
                                <tr key={session._id}>
                                    <td>{session.date?.substring(0, 10)}</td>
                                    <td>{session.starttime}</td>
                                    <td>{session.endtime}</td>
                                    <td>{session.status}</td>
                                    <td>
                                        <center>
                                            <FontAwesomeIcon
                                                color='blue'
                                                className='cursor'
                                                icon={faEdit}
                                                onClick={() => { setActiveSession(session); setSessionModal(true) }}
                                            />
                                            <FontAwesomeIcon
                                                color='red'
                                                className='cursor'
                                                style={{ marginLeft: '6px' }}
                                                icon={faTrash}
                                                onClick={() => deleteSession(session._id)}
                                            />
                                        </center>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                )}

                {savedEvent && (
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
                        <Button color='dark' onClick={() => navigate('/eventlist')}>
                            Done — Back to Events
                        </Button>
                    </div>
                )}
            </div>
        </div>
    )
}

export default CreateEvent
